import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertTriangle, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ADDON_META, ADDON_STATUS, TRIP_STEPS } from "@/lib/addons";

function Info({ k, v }) { return v ? <p className="text-xs text-white/70"><b className="text-white/90">{k}:</b> {v}</p> : null; }

function ServiceCard({ a, onUpdate }) {
  const m = ADDON_META[a.service];
  const I = m.icon;
  const trip = a.service === "ride" || a.service === "escort";
  const finish = (body) => {
    const who = window.prompt(`Who is receiving ${a.explorers.join(", ")}? Authorized: ${a.authorized || "none on file"}\n(type "ROCK" if returned to Adventure Rock)`);
    if (!who) return;
    onUpdate(a.id, who.trim().toUpperCase() === "ROCK" ? { ...body, returned_to_rock: true } : { ...body, released_to: who });
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`extra-row-${a.id}`}>
      <div className="flex items-start gap-3">
        <span className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${m.color}33` }}><I className="w-6 h-6" style={{ color: m.color }} /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold">{a.explorers.join(", ")} <span className={`ml-1 text-[10px] uppercase rounded-full px-2 py-0.5 ${ADDON_STATUS[a.status]}`} data-testid={`extra-row-status-${a.id}`}>{a.status.replace("_", " ")}</span></p>
          <p className="text-sm text-gold font-bold">{m.name} · {a.start}{a.end ? ` – ${a.end}` : ""}</p>
          <Info k="Parent" v={a.parent_name} />
          {a.parent_phone && <a href={`tel:${a.parent_phone}`} className="text-xs text-turquoise"><Phone className="w-3 h-3 inline" /> {a.parent_phone}</a>}
          <Info k="Pickup" v={a.pickup_location} /><Info k="Destination" v={a.destination} /><Info k="Return to" v={a.service === "escort" ? a.return_destination : (a.return_required ? "Return trip" : "")} />
          <Info k="Emergency" v={a.emergency_contact} /><Info k="Notes" v={a.notes} />
          <p className="text-xs text-emerald-300 flex items-center gap-1 mt-1"><ShieldCheck className="w-3 h-3" /> Release only to: {a.authorized || "—"}</p>
          {a.alerts && <p className="text-xs text-coral flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {a.alerts}</p>}
          {a.released_to && <p className="text-xs text-white/60">Released to: {a.released_to}</p>}
        </div>
      </div>
      {a.status === "requested" ? <p className="text-xs text-amber-300 mt-3">Waiting for admin confirmation.</p> : a.status !== "completed" && (
        trip ? <div className="grid grid-cols-3 gap-1.5 mt-3">{TRIP_STEPS.map(([k, l]) => (
          <button key={k} onClick={() => (k === "completed" ? finish({ trip_status: k }) : onUpdate(a.id, { trip_status: k }))} data-testid={`trip-${k}-${a.id}`}
            className={`rounded-xl py-3 text-xs font-bold ${a.trip_status === k ? "bg-gold text-navy" : k === "completed" ? "bg-emerald-500" : "bg-white/10"}`}>{l}</button>))}</div>
        : <div className="grid grid-cols-2 gap-1.5 mt-3">
          <button onClick={() => onUpdate(a.id, { status: "in_progress" })} disabled={a.status === "in_progress"} className="rounded-xl py-3 text-xs font-bold bg-sky-500 disabled:opacity-30" data-testid={`extra-start-${a.id}`}>START</button>
          <button onClick={() => (a.service === "rr" ? finish({ status: "completed" }) : onUpdate(a.id, { status: "completed", returned_to_rock: true }))} className="rounded-xl py-3 text-xs font-bold bg-emerald-500" data-testid={`extra-complete-${a.id}`}>{a.service === "rr" ? "RELEASE & COMPLETE" : "COMPLETE"}</button>
        </div>)}
    </div>
  );
}

export default function ExtraServices() {
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Atlantic/Bermuda" }));
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get(`/addons/staff/day?date=${date}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [date]);
  useEffect(() => { load(); }, [load]);
  const update = async (id, body) => { try { await api.post(`/addons/staff/${id}/status`, body); toast.success("Updated"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="min-h-16 py-2 flex flex-wrap items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm">TODAY'S EXTRA SERVICES</p></div>
        <div className="flex items-center gap-1.5">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg bg-white/10 border border-white/20 text-white px-2 py-1 text-sm" data-testid="extras-date" />
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/daytime")} className="text-white/70" data-testid="extras-back"><ArrowLeft className="w-4 h-4 mr-1" /> Rock Crew</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-4 space-y-3">
        <p className="text-xs text-white/50">Never release an Explorer to an adult who isn't on the authorized list — the app will block it.</p>
        {rows.map((a) => <ServiceCard key={a.id} a={a} onUpdate={update} />)}
        {rows.length === 0 && <p className="text-white/40" data-testid="extras-empty">No extra services for this day.</p>}
      </main>
    </div>
  );
}
